import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth } from "../../../firebase";
import LoadingCircleFacilitator from "../../utils/LoadingCircleFacilitator";
import tpcAndUSLogo from "../../../images/AndUsLoginLogo.png";
import { getRoleClaims, hasDashboardClaim } from "../../../utils/roleUtils";
import "./HeroLoginManual.css";

export default function HeroLoginManual() {
  const [email, setEmail] = useState("");
  const [password, setPw] = useState("");
  const [error, setErr] = useState("");
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [showDashboardChoice, setShowDashboardChoice] = useState(false);
  const nav = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess("");
    setBusy(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      if (!auth.currentUser.emailVerified)
        throw new Error("Please verify your e-mail first.");

      const { claims } = await auth.currentUser.getIdTokenResult(true);
      const roleClaims = getRoleClaims(claims);
      console.info("[HeroLogin] Firebase role claims:", roleClaims);
      const hasHeroAccess = hasDashboardClaim(roleClaims, "hero");
      const hasFacilitatorAccess = hasDashboardClaim(roleClaims, "facilitator");

      if (hasHeroAccess && hasFacilitatorAccess) {
        console.log(
          "🔍 [handleLogin] User has Hero and Facilitator access; awaiting dashboard selection.",
        );
        setShowDashboardChoice(true);
        return;
      }

      let successRoute = "";
      if (hasHeroAccess) successRoute = "/hero-dashboard";
      else if (hasFacilitatorAccess) successRoute = "/facilitator-dashboard";
      if (!successRoute) {
        throw new Error(
          "Your account does not have Hero or Facilitator Dashboard access.",
        );
      }
      console.log(
        "🔍 [handleLogin] Role claims:",
        roleClaims,
        "Routing to:",
        successRoute,
      );
      nav(successRoute);
    } catch (err) {
      setErr(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess("");
    setBusy(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess("Password reset email sent. Please check your inbox.");
    } catch (err) {
      setErr(err.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleForgotPassword = () => {
    setErr("");
    setSuccess("");
    setForgotPassword((prev) => !prev);
  };

  if (busy) return <LoadingCircleFacilitator />;

  return (
    <div className="hl-container">
      {/* banner */}
      <div className="hl-left">
        <div className="hl-logo-wrap">
          <img src={tpcAndUSLogo} alt="TPC" className="hl-logo" />
        </div>
        <div className="hl-welcome-wrap">
          <div className="hl-welcome-text">Welcome Back, Hero</div>
        </div>
      </div>

      {/* form */}
      <div className="hl-right">
        <form
          className="hl-form"
          onSubmit={forgotPassword ? handleForgotPassword : handleLogin}
        >
          <h2 className="hl-title">
            {forgotPassword ? "Reset Password" : "Hero Dashboard Login"}
          </h2>
          {error && <p className="hl-error">{error}</p>}
          {success && <p className="hl-success">{success}</p>}
          <input
            type="email"
            placeholder="Email"
            className="hl-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          {!forgotPassword && (
            <input
              type="password"
              placeholder="Password"
              className="hl-input"
              value={password}
              onChange={(e) => setPw(e.target.value)}
              required
            />
          )}
          <button className="hl-button" type="submit">
            {forgotPassword ? "Send Reset Email" : "Login"}
          </button>
          <div className="hl-forgot-password">
            <button
              type="button"
              className="hl-forgot-toggle"
              onClick={toggleForgotPassword}
              style={{
                background: "none",
                border: "none",
                color: "#003366",
                fontWeight: 600,
                cursor: "pointer",
                padding: 0,
              }}
            >
              {forgotPassword ? "Back to Login" : "Forgot Password?"}
            </button>
          </div>
        </form>
        <div style={{ marginTop: 24, textAlign: "center" }}>
          Not registered?{" "}
          <Link
            to="/hero-register-manual"
            style={{ fontWeight: 600, color: "#003366" }}
          >
            Register here
          </Link>
        </div>
      </div>

      {showDashboardChoice && (
        <div className="hl-dashboard-choice-overlay" role="presentation">
          <section
            className="hl-dashboard-choice"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dashboard-choice-title"
          >
            <h2 id="dashboard-choice-title">Choose your dashboard</h2>
            <p>
              Your account has access to both dashboards. Where would you like
              to continue?
            </p>
            <div className="hl-dashboard-choice-actions">
              <button type="button" onClick={() => nav("/hero-dashboard")}>
                Hero Dashboard
              </button>
              <button
                type="button"
                onClick={() => nav("/facilitator-dashboard")}
              >
                Facilitator Dashboard
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
